import React, { useState } from 'react';
import styled from 'styled-components';
import QueryString from 'qs';
import { observer } from 'mobx-react-lite';
import { themeGet } from 'styled-system';

import { Tabs, Filters, FiltersWrapper } from '@src/features/feed';
import { useSearchFilter } from '@src/hooks/useSearchFilter';
import { useStore } from '@src/models';
import { isNaN } from 'lodash';
import { Slider, TextBox, Flex, Input, Box } from 'src/kit';
import { useUpdateEffect } from 'src/hooks';

export const SubnavWithPriceSlider = observer(({ activeIndex, withSearch = true, store, generateFilters }) => {
  const [searchText, setSearchText] = useSearchFilter();

  const {
    routerStore,
    feed: {
      jobs: { getPrice }
    }
  } = useStore();

  const minPrice = Number(getPrice.min_price || 0);
  const maxPrice = Number(getPrice.max_price || 0);
  const [price, setPrice] = useState({
    min_price: 0,
    max_price: 0
  });
  const [priceStatus, setPriceStatus] = useState('initial');
  const [cachedPrice, setCachedPrice] = useState({
    min_price: 0,
    max_price: 0
  });

  React.useEffect(() => {
    if (minPrice === 0 && maxPrice === 0) return;
    setPrice({
      min_price: minPrice,
      max_price: maxPrice
    });
    setCachedPrice({
      min_price: minPrice,
      max_price: maxPrice
    });
  }, [minPrice, maxPrice]);

  useUpdateEffect(() => {
    const prevQs = QueryString.parse(routerStore.location.search, { ignoreQueryPrefix: true });

    if (priceStatus === 'initial') {
      if (prevQs.price?.min_price && prevQs.price?.max_price) {
        setPrice({
          min_price: prevQs.price?.min_price,
          max_price: prevQs.price?.max_price
        });
        setCachedPrice({
          min_price: prevQs.price?.min_price,
          max_price: prevQs.price?.max_price
        });
      }
      setPriceStatus('priceLoaded');
      return;
    }

    const newQs = QueryString.stringify(
      {
        ...prevQs,
        price: {
          min_price: price.min_price,
          max_price: price.max_price
        }
      },
      { skipNulls: true }
    );

    routerStore.push({ search: newQs });
  }, [price.max_price, price.min_price]);

  return (
    <Wrapper minWidth="182px">
      {activeIndex && <Tabs activeIndex={activeIndex} />}
      {withSearch && (
        <Input
          mt={10}
          value={searchText}
          onChange={e => {
            e.persist();
            setSearchText(e.target.value);
          }}
        />
      )}
      <Filters
        beforeOnClearAll={() => {
          setPriceStatus('initial');
          setPrice({
            min_price: minPrice,
            max_price: maxPrice
          });
          setCachedPrice({
            min_price: minPrice,
            max_price: maxPrice
          });
        }}
        generateFilters={generateFilters}
        stickyTop={80}
        store={store}
      >
        <TextBox mb={15} mt={20} color="textSecondary">
          Price (£)
        </TextBox>
        <Slider
          onAfterChange={v => {
            setPrice({
              min_price: v[0],
              max_price: v[1]
            });
            setCachedPrice({
              min_price: v[0],
              max_price: v[1]
            });
          }}
          pearling
          max={maxPrice}
          min={0}
          value={[price.min_price, price.max_price]}
          ariaLabel={['Lower price', 'Upper price']}
        />

        <Flex alignItems="center" mt={15} className="inputs">
          <Input
            type="number"
            className="input-min-price"
            label="From: "
            value={
              Number(cachedPrice.min_price)?.toLocaleString('en-GB', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
              }) || Number(0).toLocaleString('en-GB', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
            }
            width={'calc(50% - 10px)'}
            onBlur={() => {
              setPrice({
                min_price: cachedPrice.min_price,
                max_price: price.max_price || null
              });
            }}
            onChange={e =>
              setCachedPrice({
                min_price: Number(e.target.value),
                max_price: price.max_price || null
              })
            }
          />
          <Box height="1px" mx={5} width="10px" bg="iceBlueTwo" />
          <Input
            type="number"
            className="input-max-price"
            label="To: "
            value={
              Number(cachedPrice.max_price)?.toLocaleString('en-GB', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
              }) || Number(0).toLocaleString('en-GB', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
            }
            width={'calc(50% - 10px)'}
            onBlur={e => {
              if (!Boolean(e.target.value)) {
                setCachedPrice({
                  max_price: maxPrice,
                  min_price: cachedPrice.min_price
                });
                setPrice({
                  max_price: maxPrice,
                  min_price: cachedPrice.min_price
                });
                return;
              }
              if (cachedPrice.max_price < cachedPrice.min_price) {
                setCachedPrice({
                  max_price: cachedPrice.min_price,
                  min_price: cachedPrice.min_price
                });
                setPrice({
                  max_price: cachedPrice.min_price,
                  min_price: price.min_price || null
                });
                return;
              }
              setPrice({
                max_price: cachedPrice.max_price,
                min_price: price.min_price || null
              });
            }}
            onChange={e => {
              setCachedPrice({
                max_price: !isNaN(Number(e.target.value)) ? Number(e.target.value) : price.max_price,
                min_price: price.min_price || null
              });
            }}
          />
        </Flex>
      </Filters>
    </Wrapper>
  );
});

const Wrapper = styled(FiltersWrapper)`
  .inputs input[type='number']:disabled {
    color: ${themeGet('colors.iceBlueTwo')};
  }

  .inputs input {
    padding-left: 44px;
    color: ${themeGet('colors.textPrimary')};
  }
  .inputs input.input-max-price {
    padding-left: 36px;
  }
  .inputs input.input-min-price {
    padding-left: 53px;
  }
  .inputs label {
    position: relative;

    & > div {
      color: ${themeGet('colors.blueGreyTwo')};

      position: absolute;
      top: 12px;
      left: 14px;
      font-size: 14px;
    }
  }
`;
